import { useState, useEffect } from 'react'
import api from '../../utils/api'
import AdminLayout from '../../components/admin/AdminLayout'
import './Admin.css'

interface Course {
  id: number
  semester_id: number
  title: string
  instructor: string
  credits: number
  schedule: string
  capacity: number
  enrolled: number
  status: string
  deadline: string
  teaching_method: string
  period_start: string
  period_end: string
  description: string
}

export default function AdminCourseList() {
  const [courses, setCourses] = useState<Course[]>([])
  const [semesters, setSemesters] = useState<any[]>([])
  const [filterSemester, setFilterSemester] = useState<string>('')
  const [editingCourse, setEditingCourse] = useState<Course | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState({
    semesterId: '', title: '', description: '', instructor: '', credits: 0,
    teachingMethod: '', schedule: '', periodStart: '', periodEnd: '', capacity: '', deadline: ''
  })

  useEffect(() => {
    loadSemesters()
    loadCourses()
  }, [])

  useEffect(() => {
    loadCourses()
  }, [filterSemester])

  const loadSemesters = async () => {
    try {
      const res = await api.get('/semesters')
      setSemesters(res.data)
    } catch (err) {
      console.error('학기 로드 오류:', err)
    }
  }

  const loadCourses = async () => {
    try {
      const url = filterSemester ? `/admin/courses?semesterId=${filterSemester}` : '/admin/courses'
      const res = await api.get(url)
      setCourses(res.data)
    } catch (err) {
      console.error('강의 로드 오류:', err)
    }
  }

  const handleEdit = (course: Course) => {
    setEditingCourse(course)
    setEditForm({
      semesterId: course.semester_id.toString(),
      title: course.title,
      description: course.description || '',
      instructor: course.instructor,
      credits: course.credits,
      teachingMethod: course.teaching_method || '',
      schedule: course.schedule,
      periodStart: course.period_start || '',
      periodEnd: course.period_end || '',
      capacity: course.capacity.toString(),
      deadline: course.deadline || ''
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCourse) return

    try {
      await api.put(`/admin/courses/${editingCourse.id}`, editForm)
      alert('강의가 수정되었습니다.')
      setShowEditModal(false)
      setEditingCourse(null)
      loadCourses()
    } catch (err: any) {
      alert(err.response?.data?.error || '수정에 실패했습니다.')
    }
  }

  const handleDelete = async (courseId: number, title: string) => {
    if (!confirm(`"${title}" 강의를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며, 해당 강의의 수강신청 내역도 함께 삭제됩니다.`)) return
    
    try {
      await api.delete(`/admin/courses/${courseId}`)
      alert('강의가 삭제되었습니다.')
      loadCourses()
    } catch (err: any) {
      alert(err.response?.data?.error || '삭제에 실패했습니다.')
    }
  }

  const getSemesterName = (semesterId: number) => {
    const semester = semesters.find(s => s.id === semesterId)
    return semester ? `${semester.year}년 ${semester.season}` : '-'
  }

  return (
    <AdminLayout>
      <div className="admin-page">
        <h1>강의 목록</h1>

        <div className="admin-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <p style={{ margin: 0, color: '#666' }}>
                총 <strong>{courses.length}</strong>개의 강의가 등록되어 있습니다.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontWeight: 500 }}>학기 필터:</label>
              <select 
                value={filterSemester} 
                onChange={(e) => setFilterSemester(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', minWidth: '150px' }}
              >
                <option value="">전체</option>
                {semesters.map(s => (
                  <option key={s.id} value={s.id}>{s.year}년 {s.season}</option>
                ))}
              </select>
            </div>
          </div>
          
          {courses.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #dee2e6'
            }}>
              <p style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                📚 등록된 강의가 없습니다
              </p>
              <p style={{ color: '#999', margin: 0 }}>
                강의 등록 메뉴에서 새로운 강의를 등록하세요.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>학년도/계절</th>
                    <th>강좌명</th>
                    <th>교수명</th>
                    <th>학점</th>
                    <th>시간표</th>
                    <th>정원/신청</th>
                    <th>마감일</th>
                    <th>상태</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(c => (
                    <tr key={c.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{getSemesterName(c.semester_id)}</td>
                      <td><strong>{c.title}</strong></td>
                      <td>{c.instructor}</td>
                      <td style={{ textAlign: 'center' }}>{c.credits}</td>
                      <td style={{ fontSize: '0.9em' }}>{c.schedule}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ 
                          color: c.enrolled >= c.capacity ? '#dc3545' : '#28a745',
                          fontWeight: 'bold'
                        }}>
                          {c.enrolled}/{c.capacity}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.9em', whiteSpace: 'nowrap' }}>
                        {c.deadline ? new Date(c.deadline).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <span style={{ 
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          backgroundColor: c.status === 'open' ? '#d4edda' : '#f8d7da',
                          color: c.status === 'open' ? '#155724' : '#721c24',
                          whiteSpace: 'nowrap'
                        }}>
                          {c.status === 'open' ? '모집중' : '마감'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button 
                          onClick={() => handleEdit(c)} 
                          className="btn-secondary"
                          style={{ marginRight: '5px', fontSize: '0.85em', padding: '0.5rem 1rem' }}
                        >
                          수정
                        </button>
                        <button 
                          onClick={() => handleDelete(c.id, c.title)} 
                          style={{ 
                            fontSize: '0.85em', 
                            padding: '0.5rem 1rem',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 수정 모달 */}
        {showEditModal && editingCourse && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2>강의 수정</h2>
              <form onSubmit={handleUpdate}>
                <div className="form-row">
                  <label>강좌명:</label>
                  <input 
                    type="text" 
                    value={editForm.title} 
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})} 
                    required 
                  />
                </div>
                <div className="form-row">
                  <label>교수명:</label>
                  <input 
                    type="text" 
                    value={editForm.instructor} 
                    onChange={(e) => setEditForm({...editForm, instructor: e.target.value})} 
                    required 
                  />
                </div>
                <div className="form-row">
                  <label>학점:</label>
                  <input 
                    type="number" 
                    value={editForm.credits} 
                    onChange={(e) => setEditForm({...editForm, credits: parseInt(e.target.value) || 0})} 
                  />
                </div>
                <div className="form-row">
                  <label>시간표:</label>
                  <input 
                    type="text" 
                    value={editForm.schedule} 
                    onChange={(e) => setEditForm({...editForm, schedule: e.target.value})} 
                    required 
                  />
                </div>
                <div className="form-row">
                  <label>정원:</label>
                  <input 
                    type="number" 
                    value={editForm.capacity} 
                    onChange={(e) => setEditForm({...editForm, capacity: e.target.value})} 
                    required 
                  />
                </div>
                <div className="form-row">
                  <label>마감일:</label>
                  <input 
                    type="date" 
                    value={editForm.deadline} 
                    onChange={(e) => setEditForm({...editForm, deadline: e.target.value})} 
                    min="2020-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">수정</button>
                  <button type="button" onClick={() => setShowEditModal(false)} className="btn-secondary">
                    취소
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}


